import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { admin as adminApi } from '../lib/api';
import { ShieldAlert, LogOut, Loader2 } from 'lucide-react';

/**
 * Sticky top banner shown whenever the current session is an admin
 * impersonating a client. Provides one-click exit back to the admin session.
 *
 * Renders nothing when not impersonating, so it's safe to mount globally.
 */
export default function ImpersonationBanner() {
  const { user, isImpersonating, impersonatedBy, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating) return null;

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await adminApi.exitImpersonation();
      await checkAuth();
      navigate('/admin');
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message || 'Failed to exit impersonation');
    } finally {
      setExiting(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-50 w-full border-b border-[#B35900] bg-[#FFF4E5]"
      role="alert"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-3 px-4 py-2 text-sm text-[#7A3E00]">
        <ShieldAlert className="w-4 h-4 shrink-0 text-[#B35900]" />
        <p className="flex-1 truncate">
          Acting as <span className="font-semibold">{user?.name || user?.email}</span>
          {impersonatedBy ? (
            <span className="text-[#9A5A00]"> · admin: {impersonatedBy}</span>
          ) : null}
          <span className="text-[#9A5A00]"> · for support only</span>
        </p>
        <button
          onClick={handleExit}
          disabled={exiting}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-[#B35900] text-white text-xs font-medium hover:bg-[#8C4500] disabled:opacity-60"
          data-testid="exit-impersonation-btn"
        >
          {exiting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
          Exit impersonation
        </button>
      </div>
    </div>
  );
}
